// components/Modals/AddEventModal.tsx
// Manual event creation modal (NOT using Google Calendar API).
// Includes a "Generate Online Meeting Link" button directly UNDER the link input.

import React, { useState } from 'react';
import {
  X,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Video,
  Save,
  Link as LinkIcon,
  AlertCircle,
} from 'lucide-react';
import { addDays, isBefore, format } from 'date-fns';
import { googleCalendarService } from '../../services/googleCalendar';

import { OFFICE_LOCATIONS } from '../../constants/officeLocations';

/* ================= Types ================= */
export type CalendarOption = { id: string; name: string };
export type DirectoryUser = { id: string; name: string; email: string; role?: string; unitType?: string };

export type NewEventPayload = {
  calendar_id: string;
  title: string;
  description?: string;
  meeting_type?: string;
  location?: string;
  meeting_link?: string;
  attendees: { id: string; name: string; email: string }[];
  all_day: boolean;
  start_time: string; // ISO8601 (UTC)
  end_time: string;   // ISO8601 (UTC, end-exclusive recommended)
  notify_participants: boolean;
  sendEmailInvites?: boolean;
  emailMessage?: string;
  // New fields
  event_theme?: string;
  guest_name?: string;
  guest_surname?: string;
  guest_class?: string;
  guest_phone?: string;
  guest_email?: string;
  interest_department?: string;
  notes?: string;
  color?: string;
};

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: NewEventPayload) => Promise<any> | any;
  calendars?: CalendarOption[];
  defaultCalendarId?: string;
  timeZone?: string; // label only
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
}

/* ============== Helpers ============== */
function toStartISO(date: string, time: string): string {
  return new Date(`${date}T${time}:00`).toISOString();
}
function toEndISO(date: string, time: string, allDay: boolean): string {
  if (allDay) {
    const next = addDays(new Date(`${date}T00:00:00`), 1); // end-exclusive
    return next.toISOString();
  }
  return new Date(`${date}T${time}:00`).toISOString();
}

/* ============== Component ============== */
const AddEventModal: React.FC<AddEventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  calendars = [],
  defaultCalendarId,
  timeZone = 'Europe/Istanbul',
  initialDate,
  initialStartTime,
  initialEndTime,
}) => {
  const [form, setForm] = useState({
    calendar_id: defaultCalendarId || (calendars[0]?.id ?? ''),
    title: '',
    description: '',
    date: initialDate || format(new Date(), 'yyyy-MM-dd'),
    startTime: initialStartTime || '09:00',
    endTime: initialEndTime || '10:00',
    location: '',
    meetingLink: '',
    participants: [] as { id: string; name: string; email: string }[],
    sendNotification: true,
    allDay: false,
    sendEmailInvites: false,
    emailMessage: '',
    // New fields
    event_theme: 'Fiziki Randevu', // Default
    guest_name: '',
    guest_surname: '',
    guest_class: '',
    guest_phone: '',
    guest_email: '',
    interest_department: '',
    notes: '',
    color: '#BE185D', // Default
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local state for free-text input (allows spaces)
  const [guestFullName, setGuestFullName] = useState('');

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setGuestFullName('');
      setForm(prev => ({
        ...prev,
        date: initialDate || format(new Date(), 'yyyy-MM-dd'),
        startTime: initialStartTime || '09:00',
        endTime: initialEndTime || '10:00',
      }));
    }
  }, [isOpen, initialDate, initialStartTime, initialEndTime]);

  // Sync calendar_id when defaultCalendarId or calendars change
  React.useEffect(() => {
    if (!form.calendar_id && defaultCalendarId) {
      setForm(prev => ({ ...prev, calendar_id: defaultCalendarId }));
    } else if (!form.calendar_id && calendars.length > 0) {
      setForm(prev => ({ ...prev, calendar_id: calendars[0].id }));
    }
  }, [defaultCalendarId, calendars, form.calendar_id]);



  /* User selection logic removed as per request */


  async function handleGenerateLink() {
    setIsGenerating(true);
    try {
      await googleCalendarService.loadScripts();
      await googleCalendarService.authenticate();

      const startISO = new Date(`${form.date}T${form.startTime}:00`).toISOString();
      const endISO = new Date(`${form.date}T${form.endTime}:00`).toISOString();

      const link = await googleCalendarService.createMeeting(
        form.title || 'Yeni Toplantı',
        startISO,
        endISO
      );

      if (link) {
        setForm(prev => ({ ...prev, meetingLink: link }));
      } else {
        throw new Error('Link oluşturulamadı.');
      }
    } catch (e: any) {
      console.error('Google Meet Error:', e);
      let msg = e.message || 'Bir hata oluştu.';
      if (msg.includes('Client ID')) {
        alert(msg);
      } else {
        alert(`Toplantı oluşturulamadı: ${msg}`);
      }
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.calendar_id) return setError('Lütfen bir takvim seçin.');
    if (!form.title.trim()) return setError('Başlık zorunlu.');
    if (!form.allDay) {
      const start = new Date(`${form.date}T${form.startTime}:00`);
      const end = new Date(`${form.date}T${form.endTime}:00`);
      if (!isBefore(start, end)) return setError('Bitiş saati başlangıçtan sonra olmalı.');
    }
    if (form.meetingLink && !/^https?:\/\//i.test(form.meetingLink)) {
      return setError('Toplantı linki geçerli bir URL olmalıdır (http/https ile başlamalı).');
    }

    const payload: NewEventPayload = {
      calendar_id: form.calendar_id,
      title: form.title.trim(),
      description: form.description?.trim() || undefined,
      location: form.location?.trim() || undefined,
      meeting_link: form.meetingLink?.trim() || undefined,
      attendees: form.participants.map(p => ({
        id: p.id,
        name: p.name,
        email: p.email,
        user_id: p.id && !p.id.startsWith('external_') ? p.id : null
      })),
      all_day: form.allDay,
      start_time: toStartISO(form.date, form.allDay ? '00:00' : form.startTime),
      end_time: toEndISO(form.date, form.endTime, form.allDay),
      notify_participants: form.sendNotification,
      sendEmailInvites: form.sendEmailInvites,
      emailMessage: form.emailMessage,
      meeting_type: form.meetingLink ? 'online' : 'in_person',
      // New fields
      event_theme: form.event_theme,
      guest_name: form.guest_name,
      guest_surname: form.guest_surname,
      guest_class: form.guest_class,
      guest_phone: form.guest_phone,
      guest_email: form.guest_email,
      interest_department: form.interest_department,
      notes: form.notes,
      color: form.color
    };

    setIsSaving(true);
    try {
      await onSave(payload);

      // SMS Sending Logic removed from here - moved to useCalendar hook to prevent double sending

      // reset & close

      // reset & close
      setForm({
        calendar_id: defaultCalendarId || (calendars[0]?.id ?? ''),
        title: '',
        description: '',
        date: initialDate || format(new Date(), 'yyyy-MM-dd'),
        startTime: initialStartTime || '09:00',
        endTime: initialEndTime || '10:00',
        location: '',
        meetingLink: '',
        participants: [],
        sendNotification: true,
        allDay: false,
        sendEmailInvites: false,
        emailMessage: '',
        event_theme: 'Fiziki Randevu',
        guest_name: '',
        guest_surname: '',
        guest_class: '',
        guest_phone: '',
        guest_email: '',
        interest_department: '',
        notes: '',
        color: '#BE185D',
      });
      onClose();
    } catch (err: any) {
      console.error('Save failed:', err);
      setError(err?.message || 'Etkinlik kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Yeni Etkinlik</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Saat dilimi: {timeZone}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email Gönderim Ayarları */}
          {/* Email Gönderim Ayarları - HIDDEN */}
          {/* <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 hidden">
             ... 
          </div> */}

          {/* Row: Calendar & Title */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Takvim
              </label>
              <select
                value={form.calendar_id}
                onChange={(e) => setForm((prev) => ({ ...prev, calendar_id: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(calendars || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Başlık
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Örn: Öğrenci Danışmanlık Görüşmesi"
                className="w-full px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row: Date / Time / All-day */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tarih
              </label>
              <div className="relative">
                <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Başlangıç
              </label>
              <div className="relative">
                <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                  disabled={form.allDay}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bitiş
              </label>
              <div className="relative">
                <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                  disabled={form.allDay}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  id="allDay"
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, allDay: e.target.checked }))
                  }
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="allDay" className="text-sm text-gray-700 dark:text-gray-300">
                  Tüm gün
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Etkinlik Teması
              </label>
              <select
                value={form.event_theme}
                onChange={(e) => setForm((prev) => ({ ...prev, event_theme: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Fiziki Randevu">Fiziki Randevu</option>
                <option value="Online Randevu">Online Randevu</option>
                <option value="Etkinlik">Etkinlik</option>
                <option value="Telefon Görüşmesi">Telefon Görüşmesi</option>
                <option value="Toplantı">Toplantı</option>
                <option value="Vize Randevusu">Vize Randevusu</option>
                <option value="Öğlen Arası">Öğlen Arası</option>
                <option value="İptal Olan Randevu">İptal Olan Randevu</option>
              </select>
            </div>
          </div>

          {/* Conditional: Link or Location */}
          {form.event_theme === 'Online Randevu' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Online Toplantı Linki
              </label>
              <div className="relative">
                <LinkIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={form.meetingLink}
                  onChange={(e) => setForm((prev) => ({ ...prev, meetingLink: e.target.value }))}
                  placeholder="https://…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleGenerateLink}
                  disabled={isGenerating}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isGenerating
                    ? 'bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  <Video className="w-4 h-4" />
                  {isGenerating ? 'Oluşturuluyor...' : 'Google Meet Linki Oluştur (Otomatik)'}
                </button>
              </div>
            </div>
          )}

          {['Fiziki Randevu', 'Etkinlik', 'Toplantı', 'Vize Randevusu', 'Öğlen Arası'].includes(form.event_theme || '') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Konum
              </label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={form.location}
                  onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="">Konum Seçin...</option>
                  {OFFICE_LOCATIONS.map(loc => (
                    <option key={loc.id} value={loc.name}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* New Fields Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">


            {/* Guest Info */}
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Etkinlik Detayları & Misafir Bilgileri</h4>

            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Misafir Adı Soyadı
                </label>
                <input
                  type="text"
                  value={guestFullName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGuestFullName(val);

                    const parts = val.trim().split(' ');
                    if (parts.length === 1) {
                      setForm(prev => ({ ...prev, guest_name: parts[0], guest_surname: '' }));
                    } else if (parts.length > 1) {
                      const surname = parts.pop() || '';
                      const name = parts.join(' ');
                      setForm(prev => ({ ...prev, guest_name: name, guest_surname: surname }));
                    } else {
                      setForm(prev => ({ ...prev, guest_name: '', guest_surname: '' }));
                    }
                  }}
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ad Soyad"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sınıf
                </label>
                <input
                  type="text"
                  value={form.guest_class}
                  onChange={(e) => setForm((prev) => ({ ...prev, guest_class: e.target.value }))}
                  placeholder="Örn: 12. Sınıf"
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  İlgilendiği Bölüm
                </label>
                <input
                  type="text"
                  value={form.interest_department}
                  onChange={(e) => setForm((prev) => ({ ...prev, interest_department: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon Numarası
                </label>
                <input
                  type="text"
                  value={form.guest_phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, guest_phone: e.target.value }))}
                  placeholder="Örn: 5551234567"
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Misafir Email
                </label>
                <input
                  type="email"
                  value={form.guest_email}
                  onChange={(e) => setForm((prev) => ({ ...prev, guest_email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notlar
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Particpants and Users removed */}

          {/* Notifications */}
          {/* Notifications Checkbox removed */}

          {/* Errors */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Footer */}
          <div className="bg-white dark:bg-gray-900 pt-4 pb-5 mt-2 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all ${isSaving
                ? 'bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/20'
                }`}
            >
              <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEventModal;